import React from "react";
import {
  Drawer,
  Box,
  IconButton,
  InputBase,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Button,
  Stack,
} from "@mui/material";

import {
  X,
  Search,
  ChevronDown,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Rss,
} from "lucide-react";

interface MenuItemProps {
  title: string;
  href: string;
  hasDropdown?: boolean;
}

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;

  pathname: string;

  showLogin: boolean;

  searchTerm: string;

  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;

  handleNavigate: (href: string) => void;

  handleLoginClick: () => void;

  handleSearchSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

const menuItems: MenuItemProps[] = [
  { href: "/", title: "Home" },
  { href: "/review", title: "Review" },
  { href: "/pricing", title: "Pricing" },
  { href: "/resources", title: "Resources" },
  { href: "/business", title: "Business" },
  { href: "/contact", title: "Contact" },
  {
    href: "/death-notices",
    title: "Death Notices",
    hasDropdown: true,
  },
  { href: "/post-funeral", title: "Post a Funeral" },
  { href: "/socials", title: "Socials" },
];

const MobileDrawer = ({
  open,
  onClose,
  pathname,
  showLogin,
  searchTerm,
  setSearchTerm,
  handleNavigate,
  handleLoginClick,
  handleSearchSubmit,
}: MobileDrawerProps) => {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: {
            xs: "85%",
            sm: 340,
          },

          p: 3,

          bgcolor: "rgba(255,255,255,.98)",

          backdropFilter: "blur(12px)",
        },
      }}
    >
      {/* Close Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mb: 3,
        }}
      >
        <IconButton onClick={onClose}>
          <X size={24} />
        </IconButton>
      </Box>

      {/* Search */}
      <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",

            border: "1px solid rgba(0,0,0,.1)",

            borderRadius: "999px",

            px: 2,
            py: 1,
          }}
        >
          <Search size={18} />

          <InputBase
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            sx={{ ml: 1 }}
          />
        </Box>
      </Box>

      {/* Navigation */}
      <List>
        {menuItems.map((item) => {
          const isActive = pathname === item.href;

          return (
            <ListItemButton
              key={item.title}
              onClick={() => handleNavigate(item.href)}
              sx={{
                borderRadius: 2,

                mb: 1,

                bgcolor: isActive ? "rgba(122,92,72,.08)" : "transparent",

                "&:hover": {
                  bgcolor: "rgba(122,92,72,.08)",
                },
              }}
            >
              <ListItemText
                primary={item.title}
                primaryTypographyProps={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: isActive ? "#7A5C48" : "#444",
                }}
              />

              {item.hasDropdown && <ChevronDown size={18} />}
            </ListItemButton>
          );
        })}

        {/* Login */}
        {showLogin && (
          <Box mt={3}>
            <Button
              fullWidth
              onClick={handleLoginClick}
              sx={{
                border: "1px solid rgba(0,0,0,.15)",

                borderRadius: "999px",

                py: 1.25,

                color: "#000",

                fontWeight: 700,

                textTransform: "none",

                "&:hover": {
                  bgcolor: "#6F4C3E",

                  color: "#fff",

                  borderColor: "#6F4C3E",
                },
              }}
            >
              Login
            </Button>
          </Box>
        )}
      </List>

      {/* Divider */}
      <Box
        sx={{
          my: 4,
          borderTop: "1px solid rgba(0,0,0,.08)",
        }}
      />

      {/* Contact */}
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Phone size={16} />

          <Typography
            sx={{
              fontSize: 14,
              color: "#666",
            }}
          >
            +263 774 808 392
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Mail size={16} />

          <Typography
            sx={{
              fontSize: 14,
              color: "#666",
            }}
          >
            sales@muzukuru.com
          </Typography>
        </Stack>
      </Stack>

      {/* Socials */}
      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <IconButton>
          <Facebook size={18} />
        </IconButton>

        <IconButton>
          <Typography
            sx={{
              fontWeight: 700,
            }}
          >
            X
          </Typography>
        </IconButton>

        <IconButton>
          <Instagram size={18} />
        </IconButton>

        <IconButton>
          <Rss size={18} />
        </IconButton>
      </Stack>
    </Drawer>
  );
};

export default MobileDrawer;
